import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
  type ReactNode,
} from 'react';
import { api, setAccessToken } from '../api/client';

/** Preferencias de ayuda interactiva del usuario (se cambian con PATCH auth/me/preferencias). */
export interface PreferenciasAyuda {
  /** Guías paso a paso en los formularios; encendida por defecto. */
  ayudaInteractiva: boolean;
  /** Ids de las guías que pidió no volver a ver. */
  ayudaOcultas: string[];
}

interface User extends PreferenciasAyuda {
  id: number;
  username: string;
  role: string;
  roleId: number;
  permisos: string[];
}

/** Preferencias de ayuda de una respuesta del backend: encendida y sin ocultas si no vienen. */
const leerPreferenciasAyuda = (u: any): PreferenciasAyuda => ({
  ayudaInteractiva: typeof u?.ayudaInteractiva === 'boolean' ? u.ayudaInteractiva : true,
  ayudaOcultas: Array.isArray(u?.ayudaOcultas) ? u.ayudaOcultas.filter((g: unknown) => typeof g === 'string') : [],
});

interface AuthContextType {
  user: User | null;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  isAuthenticated: boolean;
  /** Check if the current user has a specific granular permission. */
  can: (modulo: string, accion: string) => boolean;
  /** Raw permisos Set for direct access (e.g. sidebar filtering). */
  permisos: Set<string>;
  /** True while the silent session bootstrap is in progress. */
  loading: boolean;
  /**
   * Cambia las preferencias de ayuda del usuario actual: se ven enseguida y, si el backend falla, vuelven a como
   * estaban y la promesa se rechaza (quien llama avisa).
   */
  actualizarPreferencias: (cambios: Partial<PreferenciasAyuda>) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

const API_BASE = import.meta.env.VITE_API_BASE || '';

/**
 * Recupera la sesión con la cookie de refresh. Una sola llamada en vuelo: el backend rota el refresh token y revoca el
 * anterior, así que una segunda llamada concurrente (StrictMode en desarrollo monta el efecto dos veces) daría 401.
 */
let arranque: Promise<User | null> | null = null;
function recuperarSesion(): Promise<User | null> {
  arranque ??= (async () => {
    try {
      const res = await fetch(`${API_BASE}/api/v3/auth/refresh`, { method: 'POST', credentials: 'include' });
      if (!res.ok) return null;
      const data = await res.json();
      setAccessToken(data.access_token);
      const meRes = await fetch(`${API_BASE}/api/v3/auth/me`, {
        headers: { Authorization: `Bearer ${data.access_token}` },
        credentials: 'include',
      });
      if (!meRes.ok) return null;
      const meData = await meRes.json();
      return {
        id: meData.id,
        username: meData.username,
        role: meData.role,
        roleId: meData.roleId,
        permisos: Array.isArray(meData.permisos) ? meData.permisos : [],
        ...leerPreferenciasAyuda(meData),
      };
    } catch {
      return null; // sin sesión: se muestra el login
    } finally {
      arranque = null;
    }
  })();
  return arranque;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  // User info is stored in memory only (not localStorage).
  // The access token is managed by client.ts (also in-memory).
  // The refresh token is an httpOnly cookie (not accessible from JS).
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Build a fast-lookup Set from user.permisos (empty set when no user).
  const permisos: Set<string> = user?.permisos
    ? new Set(user.permisos)
    : new Set<string>();

  const can = (modulo: string, accion: string): boolean => {
    return permisos.has(`${modulo}:${accion}`);
  };

  // ── Silent session bootstrap on mount ─────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    recuperarSesion().then(u => {
      if (cancelled) return;
      if (u) setUser(u);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    const res = await fetch(`${API_BASE}/api/v3/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include', // receive refresh cookie
      body: JSON.stringify({ username, password }),
    });

    if (!res.ok) {
      if (res.status === 429) {
        const data = await res.json().catch(() => null);
        const retryAfter = data?.retryAfter ?? 60;
        throw new Error(
          `Demasiados intentos. Esperá ${retryAfter} segundos.`,
        );
      }
      throw new Error('Credenciales inválidas');
    }

    const data = await res.json();

    // Store access token in memory (via client.ts)
    setAccessToken(data.access_token);

    // Parser: backend returns permisos as string[] inside user object
    const permisosArray: string[] = Array.isArray(data.user?.permisos)
      ? data.user.permisos
      : [];
    setUser({
      ...data.user,
      permisos: permisosArray,
      roleId: data.user?.roleId ?? data.roleId,
      ...leerPreferenciasAyuda(data.user),
    });
  }, []);

  const userRef = useRef(user);
  userRef.current = user;
  const actualizarPreferencias = useCallback(async (cambios: Partial<PreferenciasAyuda>) => {
    const actual = userRef.current;
    if (!actual) return;
    const previas: Partial<PreferenciasAyuda> = {};
    for (const clave of Object.keys(cambios) as (keyof PreferenciasAyuda)[]) {
      (previas as Record<string, unknown>)[clave] = actual[clave];
    }
    setUser(u => (u ? { ...u, ...cambios } : u));
    try {
      await api.patch('/api/v3/auth/me/preferencias', cambios);
    } catch (e) {
      setUser(u => (u ? { ...u, ...previas } : u));
      throw e;
    }
  }, []);

  const logout = useCallback(async () => {
    // Call logout endpoint to blacklist tokens
    try {
      await fetch(`${API_BASE}/api/v3/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch {
      // Proceed even if the call fails
    }

    // Clear in-memory state
    setUser(null);
    setAccessToken(null);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        logout,
        isAuthenticated: !!user,
        can,
        permisos,
        loading,
        actualizarPreferencias,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
